import * as React from "react"
import { Check } from "lucide-react"
import { Badge } from "@/components/ui/Badge"
import { Button } from "@/components/ui/Button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover"
import type { OrgNode } from "../Index"

interface Props {
  channels: OrgNode[]
  selectedIds: string[]
  nodeLabel: (node: OrgNode) => string
  disabled?: boolean
  onSave: (nodeIds: string[]) => void
}

// Popover-based multi-select: pick which sale channels a warehouse serves.
// Selections are staged locally and only committed on explicit Save;
// closing without saving discards the in-progress change.
export default function ChannelPicker({ channels, selectedIds, nodeLabel, disabled, onSave }: Props) {
  const [open, setOpen] = React.useState(false)
  const [selected, setSelected] = React.useState<Set<string>>(new Set(selectedIds))

  function handleOpenChange(next: boolean) {
    if (next) setSelected(new Set(selectedIds))
    setOpen(next)
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function save() {
    onSave([...selected])
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className="h-auto min-h-9 w-full justify-start px-3 py-1.5 font-normal"
        >
          {selectedIds.length === 0 ? (
            <span className="text-muted-foreground">— Unallocated</span>
          ) : (
            <span className="flex flex-wrap gap-1">
              {selectedIds.map((id) => (
                <Badge key={id} variant="secondary">
                  {channels.find((c) => c.id === id)?.name ?? "?"}
                </Badge>
              ))}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-1.5">
        <div className="max-h-64 overflow-y-auto">
          {channels.map((node) => {
            const checked = selected.has(node.id)
            return (
              <button
                key={node.id}
                type="button"
                onClick={() => toggle(node.id)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                  {checked && <Check className="h-4 w-4" />}
                </span>
                {nodeLabel(node)}
              </button>
            )
          })}
          {channels.length === 0 && (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              No sale channels in this market.
            </div>
          )}
        </div>
        <div className="flex justify-end pt-2 mt-1 border-t border-border">
          <Button size="sm" onClick={save}>
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
